<template>
  <div>
    <ColorBanner/>
    <v-container class="mt-10" style="max-width: 768px;"> 
      <v-card shaped v-if="isLoading">
        <v-skeleton-loader type="image"></v-skeleton-loader>
      </v-card>
      <v-card v-else shaped>
        <v-card-title>Role Eligibility Application</v-card-title>
        <v-card-text v-if="!userEmployeeRecord.fields['Role Eligibility']">
          <v-row>
            <v-col>
              <p>👋 Your role: {{ userEmployeeRecord.fields.Title }} on {{ computedDepartment }} does not have a role eligibility decision yet. Check back later!</p>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-text v-else>
          <v-row>
            <v-col>
              <p>👋 Your role: {{ userEmployeeRecord.fields.Title }} on {{ computedDepartment }} is eligible for {{ userEmployeeRecord.fields['Role Eligibility'] }} work.</p>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-list>
                <v-subheader>Role Eligibility Descriptions</v-subheader>
                <v-list-item two-line v-for="eligibility in eligibilities" :key="eligibility.title">
                  <v-list-item-icon>
                    <v-icon v-text="eligibility.icon"></v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ eligibility.title }}</v-list-item-title>
                    <v-card-text class="pa-0 grey--text text--darken-2">
                      {{ eligibility.description }}
                      <v-spacer></v-spacer>
                      <small v-if="eligibility.finePrint">{{ eligibility.finePrint }}</small>
                    </v-card-text>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
          <v-row v-if="!userEmployeeRecord.fields['Preferred Role Eligibility']">
            <v-col :class="{'ma-0': $vuetify.breakpoint.smAndDown, 'ma-5': $vuetify.breakpoint.mdAndUp}">
              <v-row>
                <v-col>
                  <strong>Select your preference based on your role's available eligibilities:</strong>
                </v-col>              
              </v-row>
              <v-row>
                <v-col>
                  <v-select
                    rounded
                    clearable
                    solo
                    v-model="eligibilitySelection"
                    :items="eligibilityOptions"
                  ></v-select>
                </v-col>
              </v-row>
              <v-row v-if="showDaysOfWeek">
                <v-col>
                  <strong>
                    What days of the week do you prefer to come into the office?
                  </strong>
                  <p class="mb-0"><small>Understand that the business may ask you to come in on days outside of your preference as needed.</small></p>
                </v-col>
              </v-row>
              <v-row v-if="showDaysOfWeek">
                <v-col class="pt-0">
                  <v-checkbox v-model="monday" label="Monday"></v-checkbox>
                </v-col>
                <v-col class="pt-0">
                  <v-checkbox v-model="tuesday" label="Tuesday"></v-checkbox>
                </v-col>
                <v-col class="pt-0">
                  <v-checkbox v-model="wednesday" label="Wednesday"></v-checkbox>
                </v-col>
                <v-col class="pt-0">
                  <v-checkbox v-model="thursday" label="Thursday"></v-checkbox>
                </v-col>
                <v-col class="pt-0">
                  <v-checkbox v-model="friday" label="Friday"></v-checkbox>
                </v-col>
              </v-row>
              <v-row v-if="showFrequency">
                <v-col>
                  <strong>
                    How often do you see yourself coming into the office?
                  </strong>
                  <p class="mb-0"><small>Understand that the business may ask you to come in more frequently as needed.</small></p>
                </v-col>
              </v-row>
              <v-row v-if="showFrequency">
                <v-col>
                  <v-select
                    rounded
                    clearable
                    solo
                    v-model="inOfficeFrequencySelection"
                    :items="inOfficeFrequencyOptions"
                  ></v-select>
                </v-col>
              </v-row>
              <v-row>
                <v-col class="d-flex justify-end">
                  <v-btn :class="{'d-none': $vuetify.breakpoint.smAndDown }"
                  text rounded @click="clear" :disabled="clearDisabled">
                    Clear Selection
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn 
                    color="primary" 
                    rounded 
                    @click="save"
                    :loading="saveLoading"
                    :disabled="saveDisabled"
                  >
                    Submit Role Eligiblity Preference
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row v-else>
            <v-row>
              <v-col class="pb-0">
                <v-alert type="success">
                  <v-row>
                    <v-col class="pt-0">
                      Thanks for sharing your frequency preferences, this will help us plan the future of the work space.
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col class="pt-0">
                      You have selected {{ userEmployeeRecord.fields['Preferred Role Eligibility'] }}
                    </v-col>
                  </v-row>
                  <v-row v-if="showDeskBookMsg">
                    <v-col class="pt-0">
                      You will still need to book your desk on an as needed basis when you to come in.
                    </v-col>
                  </v-row>
                  <v-row v-if="showFrequency">
                    <v-col class="pt-0">
                      In-Office projected frequency: {{ inOfficeFrequencySelection }}
                    </v-col>
                  </v-row>
                  <v-row v-if="showDaysOfWeek">
                    <v-col class="pt-0">
                      <v-row>
                        <v-col class="pt-0 pb-0 d-flex align-center">
                          <v-icon left>{{ getCheckboxIcon(monday) }}</v-icon> Monday
                        </v-col>
                        <v-col class="pt-0 pb-0 d-flex align-center">
                          <v-icon left>{{ getCheckboxIcon(tuesday) }}</v-icon> Tuesday
                        </v-col>
                        <v-col class="pt-0 pb-0 d-flex align-center">
                          <v-icon left>{{ getCheckboxIcon(wednesday) }}</v-icon> Wednesday
                        </v-col>
                        <v-col class="pt-0 pb-0 d-flex align-center">
                          <v-icon left>{{ getCheckboxIcon(thursday) }}</v-icon> Thursday
                        </v-col>
                        <v-col class="pt-0 pb-0 d-flex align-center">
                          <v-icon left>{{ getCheckboxIcon(friday) }}</v-icon> Friday
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-alert>
              </v-col>
              <v-col cols="12" class="pt-0 d-flex justify-end">
                <small>If you need to make changes regarding your preference, please reach out to your HR Business Partner.</small>
              </v-col>
            </v-row>
          </v-row>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import ColorBanner from '@/components/Banner'
import User from '@/mixins/User'

export default {
  name: 'RoleEligibilityApplication',

  mixins: [ User ],

  components: {
    ColorBanner
  },

  data: () => ({
    eligibilities: [
      {
        title: 'Remote',
        icon: 'mdi-home-variant-outline',
        description: 'The employee works full-time from home - they will not be assigned a base office.',
        finePrint: 'States eligible for remote work: CA, CO, CT, DC, FL, IL, MA, MD, MN, MO, NC, NY, OR, PA, TX, UT, VA, VT'
      },
      {
        title: 'Open-Flex',
        icon: 'mdi-home-city-outline',
        description: 'While the employee is assigned to a base office, they do not have a fixed or regular work pattern. Employee is to be available in office as needed by the business with the employee being responsible for their commute as they would be under an in-office designation.  Otherwise employee may reserve space in office as they choose with no minimum or maximum in-office requirements as long as business need is met.'
      },
      {
        title: 'Fixed-Flex',
        icon: 'mdi-home-city-outline',
        description: 'The employee works partially from home and partially from the office on a fixed schedule.',
        finePrint: 'i.e. Monday/Wednesday/Friday in office'
      },
      {
        title: 'In-Office',
        icon: 'mdi-office-building-outline',
        description: 'Employee works full time from their base office.'
      },
    ],
    eligibilitySelection: null,
    inOfficeFrequencySelection: null,
    inOfficeFrequencyOptions: [ 'Less than once per week', 'Once per week', 'Twice per week', 'Three times per week', 'More than three times per week' ],
    saveLoading: false,
    monday: null,
    tuesday: null,
    wednesday: null,
    thursday: null,
    friday: null
  }),

  async created () {
    await this.$store.dispatch('setUserEmployeeRecord')
    if (this.userEmployeeRecord.fields['Role Eligibility']) {
      this.eligibilitySelection = this.userEmployeeRecord.fields['Preferred Role Eligibility']
      this.monday = this.userEmployeeRecord.fields['Monday']
      this.tuesday = this.userEmployeeRecord.fields['Tuesday']
      this.wednesday = this.userEmployeeRecord.fields['Wednesday']
      this.thursday = this.userEmployeeRecord.fields['Thursday']
      this.friday = this.userEmployeeRecord.fields['Friday']
      this.inOfficeFrequencySelection = this.userEmployeeRecord.fields['In-Office Frequency']
    }
  },

  computed: {
    clearDisabled () {
      return this.saveLoading || !this.eligibilitySelection
    },
    computedDepartment () {
      if (!this.userEmployeeRecord.fields.Department) return 'your team'
      let computedDept = this.userEmployeeRecord.fields.Department
      console.log(computedDept.startsWith('Shared '))
      if (computedDept.startsWith('Shared ')) {
        computedDept = computedDept.split('Shared ')[1]
      }
      return 'the ' + computedDept + ' team'
    },
    isLoading () {
      return !this.userEmployeeRecord
    },
    eligibilityOptions () {
      let options = [ 'Remote', 'Open-Flex', 'Fixed-Flex', 'In Office' ]
      switch (this.userEmployeeRecord.fields['Role Eligibility']) {
        case 'Remote':
          return options
        case 'Open-Flex':
          return options.filter(o => o !== 'Remote')
        case 'Fixed-Flex':
          return options.filter(o => o !== 'Remote' && o !== 'Open-Flex')
        default:
          return [ 'In Office' ]
      }
    },
    noDaySelected () {
      return !this.monday && !this.tuesday && !this.wednesday && !this.thursday && !this.friday
    },
    saveDisabled () {
      const requiresDaysOfWeek = this.showDaysOfWeek && this.noDaySelected
      const requiresFrequency = this.showFrequency && !this.inOfficeFrequencySelection
      return !this.eligibilitySelection || requiresDaysOfWeek || requiresFrequency
    },
    showDeskBookMsg () {
      return this.eligibilitySelection === 'Open-Flex'
    },
    showDaysOfWeek () {
      return this.eligibilitySelection === 'Open-Flex'
    },
    showFrequency () {
      return this.eligibilitySelection === 'Open-Flex'
    }
  },

  methods: {
    clear () {
      this.eligibilitySelection = null
    },
    getCheckboxIcon (bool) {
      return bool ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'
    },
    async save () {
      console.log('saving')
      this.saveLoading = true
      let fields = {
        'Preferred Role Eligibility': this.eligibilitySelection
      }
      if (this.showDaysOfWeek) {
        fields['Monday'] = this.monday
        fields['Tuesday'] = this.tuesday
        fields['Wednesday'] = this.wednesday
        fields['Thursday'] = this.thursday
        fields['Friday'] = this.friday
      }
      if (this.showFrequency) {
        fields['In-Office Frequency'] = this.inOfficeFrequencySelection
      }
      await this.$store.dispatch('updateRecords', {
        table: 'Employees',
        records: [{ id: this.userEmployeeRecord.id, fields: fields }]
      })
      await this.$store.dispatch('getRecords', {
        table: 'Employees',
        view: 'All Employees',
        dispatch: 'setEmployees'
      })
      await this.$store.dispatch('setUserEmployeeRecord')

      this.$store.dispatch('setSnackbar', {
        snackbarColor: 'success',
        showSnackbar: true,
        snackbarY: 'top',
        snackbarX: 'right',
        snackbarText: 'Your role eligibility preference has been saved'
      })
      this.saveLoading = false
    }
  }
}
</script>